import { useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  Box, Button, FormControl, FormHelperText, FormLabel, Grid, GridItem, Heading,
  NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, Text
} from '@chakra-ui/react'
import {
  formatFeetInches, formatSquareFootage, getHeightBreakdown, isTruthyFlag, STACK_MODE_LABELS, syncStructureFields
} from 'lib'

export const EdgeOverallStructureForm = ({ form, setForm, ...rest }) => {
  const synced = useMemo(() => syncStructureFields(form), [
    form.noPedestal, form.pedestalHeightIn, form.noEmc, form.emcModsH, form.emcModsL, form.emcStackMode,
    form.noCabinet, form.cabinetHeightIn, form.cabinetLengthIn, form.cabinetStackMode, form.structureComponents,
    form.groundClearanceFt, form.groundClearanceManual, form.overallHeightFt, form.overallHeightManual,
    form.squareFootage, form.squareFootageManual, form.emcHeightFt, form.emcLengthFt, form.emcNominalSize,
    form.cabinetHeightFt, form.cabinetNominalSize
  ])
  const breakdown = useMemo(() => getHeightBreakdown({ ...form, groundClearanceFt: synced.groundClearanceFt }), [
    form, synced.groundClearanceFt
  ])

  const handleNumericChange = (field, manualField, value) => {
    setForm(draft => {
      draft[field] = value === '' ? '' : parseFloat(value)
      draft[manualField] = true
    })
  }

  const resetField = (manualField) => {
    setForm(draft => {
      draft[manualField] = false
    })
  }

  return (
    <Box {...rest}>
      <Grid
        templateColumns="5fr 1fr"
        rowGap={5}
        columnGap={10}
        w="900px"
        maxW="100%"
      >
        <GridItem>
          <Heading size="md">Overall Structure Height</Heading>
          <Text fontSize="sm" color="gray.600" mt={1} mb={3}>
            Overall height = ground clearance + stacked component heights. Side-by-side components are excluded from height but still count in square footage. These values are stored on the project details, not printed on the quote PDF.
          </Text>
          <Grid templateColumns="repeat(3, 1fr)" gap={4}>
            <GridItem>
              <FormControl>
                <FormLabel>Ground Clearance (ft)</FormLabel>
                <NumberInput
                  min={0}
                  precision={2}
                  step={0.01}
                  value={form.groundClearanceFt ?? ''}
                  onChange={(v) => handleNumericChange('groundClearanceFt', 'groundClearanceManual', v)}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  {form.noPedestal
                    ? 'No pedestal — enter estimated clearance, or leave 0.'
                    : `Auto from pedestal/pole cover (${formatFeetInches(synced.calculatedGroundClearanceFt)}). Overridable.`}
                </FormHelperText>
                {isTruthyFlag(form.groundClearanceManual) && (
                  <Button size="xs" variant="link" mt={1} onClick={() => resetField('groundClearanceManual')}>
                    Reset to calculated
                  </Button>
                )}
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>Overall Height (ft)</FormLabel>
                <NumberInput
                  min={0}
                  precision={2}
                  step={0.01}
                  value={form.overallHeightFt ?? ''}
                  onChange={(v) => handleNumericChange('overallHeightFt', 'overallHeightManual', v)}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  Calculated {formatFeetInches(synced.calculatedOverallHeightFt)}. Always overridable.
                </FormHelperText>
                {isTruthyFlag(form.overallHeightManual) && (
                  <Button size="xs" variant="link" mt={1} onClick={() => resetField('overallHeightManual')}>
                    Reset to calculated
                  </Button>
                )}
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>Square Footage</FormLabel>
                <NumberInput
                  min={0}
                  precision={2}
                  step={0.01}
                  value={form.squareFootage ?? ''}
                  onChange={(v) => handleNumericChange('squareFootage', 'squareFootageManual', v)}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  Height × length in feet. Calculated {formatSquareFootage(synced.calculatedSquareFootage)}.
                </FormHelperText>
                {isTruthyFlag(form.squareFootageManual) && (
                  <Button size="xs" variant="link" mt={1} onClick={() => resetField('squareFootageManual')}>
                    Reset to calculated
                  </Button>
                )}
              </FormControl>
            </GridItem>
          </Grid>
          <Box mt={4} bg="gray.50" p={3} borderRadius="md">
            <Text fontSize="sm" fontWeight="semibold" mb={1}>Height breakdown</Text>
            {breakdown.map(row => (
              <Text key={row.key} fontSize="sm" color={row.included ? 'gray.700' : 'gray.500'}>
                {row.included ? '+' : 'excluded'} {row.label}
                {row.stackMode ? ` (${STACK_MODE_LABELS[row.stackMode]})` : ''}: {formatFeetInches(row.heightFt)}
              </Text>
            ))}
            <Text fontSize="sm" fontWeight="semibold" mt={2}>
              Overall height {formatFeetInches(synced.overallHeightFt)} · Sign area {formatSquareFootage(synced.squareFootage)}
            </Text>
          </Box>
        </GridItem>
        <GridItem></GridItem>
      </Grid>
    </Box>
  )
}

EdgeOverallStructureForm.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func
}
